import { useNavigate, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import { Button } from '@/components/ui/Button'
import { UserAvatar } from '@/components/UserAvatar'
import { FrontFlowLogo } from '@/components/brand/FrontFlowLogo'
import { LogOut, User, ShieldCheck, Wrench } from 'lucide-react'
import { isFrontFlowOrg } from '@/constants/frontflowOrg'
import { getAuthenticatedUserProfile } from '@/services/api'
import { isAdminEligible } from '@/types/api'
import { useActingAs } from '@/state/actingAs'

/**
 * Top navigation bar: logo (to root) and the profile menu. Its bottom edge is the
 * navy canvas edge, or a light line when a product tab bar (data-subnav) sits below.
 */
export function AuthenticatedLayoutNav() {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const { data: profile } = useQuery({
    queryKey: ['user-profile'],
    queryFn: getAuthenticatedUserProfile,
  })
  // Hidden while acting as another org: that view is meant to match what the
  // acted-as org itself would see, and no ordinary org has this entry.
  const actingAs = useActingAs()
  const canAdminister = !actingAs && isAdminEligible(profile?.user.organization)
  const showDevTools = isFrontFlowOrg(user?.tenantId)

  const handleSignOut = async () => {
    try {
      await signOut()
    } catch (error) {
      console.error('Sign out failed:', error)
    }
  }

  // The name the avatar is drawn and coloured from. Falling back to the email address keeps a
  // user whose token carries no name looking like everyone else rather than like a question mark,
  // and gives them a colour of their own the same way.
  const avatarFirstName = user?.given_name ?? user?.email ?? ''
  const avatarLastName = user?.given_name ? (user?.family_name ?? '') : ''

  return (
    <nav className="fixed top-0 z-50 w-full border-b border-canvas-border [body:has([data-subnav])_&]:border-gray-200 bg-white dark:bg-gray-900">
      <div className="mx-auto max-w-app px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <div className="flex shrink-0 items-center gap-2">
            <Link to="/" className="flex items-center" aria-label="FrontFlow home">
              <FrontFlowLogo />
            </Link>
          </div>
          <div className="flex items-center gap-2 md:gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="relative h-8 w-8 rounded-full focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  aria-label="Open user menu"
                >
                  <span className="absolute -inset-1.5" aria-hidden />
                  <UserAvatar
                    firstName={avatarFirstName}
                    lastName={avatarLastName}
                    size="md"
                    className="ring-2 ring-white dark:ring-gray-900"
                  />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <User className="mr-2 h-4 w-4" />
                  Your profile
                </DropdownMenuItem>
                {canAdminister && (
                  <DropdownMenuItem onClick={() => navigate('/admin')}>
                    <ShieldCheck className="mr-2 h-4 w-4" />
                    Admin
                  </DropdownMenuItem>
                )}
                {showDevTools && (
                  <DropdownMenuItem onClick={() => navigate('/dev-tools')}>
                    <Wrench className="mr-2 h-4 w-4" />
                    Developer tools
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={handleSignOut}
                  className="text-red-600 focus:text-red-600 dark:text-red-400 dark:focus:text-red-400"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </nav>
  )
}
